import { getIdToken } from 'src/services/authService';
import { spreadSheetApiConfig } from 'src/services/pages/Documents/Auxiliares/spreadSheetApi';
import { JobNotificationDto, FileUploadJobDto, TransactionLogPagedDto, TransactionLogFilters, TransactionLogStatusOptionDto } from 'src/models/interfaces/INotification';
import { parseApiError } from 'src/utils/apiErrorHandler';

const NOTIFICATION_BASE = spreadSheetApiConfig.baseUrl;

/**
 * Fetches all unread notifications for the authenticated user.
 * The backend resolves the UserId automatically from the Bearer token.
 */
export async function getUnreadNotifications(): Promise<JobNotificationDto[]> {
  const idToken = await getIdToken();
  const response = await fetch(`${NOTIFICATION_BASE}/api/Notification/unread`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
      Accept: 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    const apiError = await parseApiError(response);
    throw apiError;
  }

  return response.json() as Promise<JobNotificationDto[]>;
}

/**
 * Marks a single notification as read.
 * @param notificationId - The id of the notification to mark as read.
 */
export async function markNotificationAsRead(notificationId: number): Promise<void> {
  const idToken = await getIdToken();
  const response = await fetch(`${NOTIFICATION_BASE}/api/Notification/${notificationId}/read`, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    const apiError = await parseApiError(response);
    throw apiError;
  }
}

export async function getJobHistory(): Promise<FileUploadJobDto[]> {
  const idToken = await getIdToken();
  const response = await fetch(`${NOTIFICATION_BASE}/api/WkldSheetData/jobs`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
      Accept: 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    const apiError = await parseApiError(response);
    throw apiError;
  }

  return response.json() as Promise<FileUploadJobDto[]>;
}

/**
 * Fetches paginated transaction log from GET /api/Workload/transaction-log.
 */
export async function getTransactionLog(filters: TransactionLogFilters): Promise<TransactionLogPagedDto> {
  const idToken = await getIdToken();

  const params = new URLSearchParams();
  params.set('pageNumber', String(filters.pageNumber));
  params.set('pageSize', String(filters.pageSize));
  if (filters.searchTerm) params.set('searchTerm', filters.searchTerm);
  filters.statuses?.forEach((s) => params.append('statuses', String(s)));
  filters.modules?.forEach((m) => params.append('modules', m));

  const response = await fetch(`${NOTIFICATION_BASE}/api/Workload/transaction-log?${params.toString()}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
      Accept: 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    const apiError = await parseApiError(response);
    throw apiError;
  }

  return response.json() as Promise<TransactionLogPagedDto>;
}

/**
 * Fetches available module options for the transaction log filter.
 * GET /api/Workload/transaction-log/modules
 */
export async function getTransactionLogModules(): Promise<string[]> {
  const idToken = await getIdToken();
  const response = await fetch(`${NOTIFICATION_BASE}/api/Workload/transaction-log/modules`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
      Accept: 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    const apiError = await parseApiError(response);
    throw apiError;
  }

  return response.json() as Promise<string[]>;
}

/**
 * Fetches available status options for the transaction log filter.
 * GET /api/Workload/transaction-log/statuses
 */
export async function getTransactionLogStatuses(): Promise<TransactionLogStatusOptionDto[]> {
  const idToken = await getIdToken();
  const response = await fetch(`${NOTIFICATION_BASE}/api/Workload/transaction-log/statuses`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json; charset=utf-8',
      Accept: 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    const apiError = await parseApiError(response);
    throw apiError;
  }

  return response.json() as Promise<TransactionLogStatusOptionDto[]>;
}

/**
 * Triggers the download of a completed export job file.
 * Fetches the blob with auth headers and triggers a browser download.
 * @param jobId - The id of the completed export job.
 * @param fileName - The suggested file name for the download.
 */
export async function downloadExportJob(jobId: number, fileName: string): Promise<void> {
  const idToken = await getIdToken();
  const response = await fetch(`${NOTIFICATION_BASE}/api/workload/jobs/${jobId}/download`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  });

  if (!response.ok) {
    const apiError = await parseApiError(response);
    throw apiError;
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
}
